import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { createGuide, publishGuide } from '../../utils/guideOperations';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';

const SimpleWysiwygEditor = ({ itemId, initialContent = '', initialTitle = '', onSave, onClose, itemName = '' }) => {
  const { currentUser, userDetails } = useAuth();
  const [title, setTitle] = useState(initialTitle || `${itemName} Guide`);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && initialContent) {
      // Sanitize HTML content before rendering to prevent XSS attacks
      const sanitizedContent = DOMPurify.sanitize(initialContent, {
        ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                       'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br', 'div', 'span', 'blockquote', 'pre', 'code'],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'style', 'class', 'target', 'rel', 'border'],
        ALLOWED_STYLES: {
          '*': {
            'color': [/.*/],
            'text-align': [/.*/],
            'font-size': [/.*/],
            'font-weight': [/.*/],
            'padding': [/.*/],
            'margin': [/.*/],
            'background-color': [/.*/],
            'border-collapse': [/.*/],
            'width': [/.*/],
            'height': [/.*/],
            'max-width': [/.*/],
            'max-height': [/.*/]
          }
        },
        ALLOW_DATA_ATTR: false
      });
      editorRef.current.innerHTML = sanitizedContent;
    }
  }, [initialContent]);

  const handleSave = async () => {
    if (!editorRef.current) return;

    setSaving(true);
    try {
      const content = editorRef.current.innerHTML;
      const guideData = {
        title: title || `${itemName} Guide`,
        content,
        authorId: currentUser?.uid || 'anonymous',
        authorName: userDetails?.name || currentUser?.displayName || 'Anonymous'
      };

      // Create guide as draft
      const createdGuide = await createGuide(itemId, guideData);

      // Immediately publish it
      await publishGuide(
        createdGuide.id,
        currentUser?.uid || 'anonymous',
        userDetails?.name || currentUser?.displayName || 'Anonymous'
      );

      onSave && onSave();
    } catch (error) {
      console.error('Error saving guide:', error);
      alert('Error saving guide: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image file is too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const filename = `guide_images/${itemId}_${timestamp}.${fileExtension}`;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, filename);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Insert image into editor
      const imgHtml = `<img src="${downloadURL}" alt="Guide image" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      execCommand('insertHTML', imgHtml);
      
      // Clear file input
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const insertTable = () => {
    const html = `
      <table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">
        <thead>
          <tr>
            <th style="padding: 8px; background-color: #f5f5f5;">Header 1</th>
            <th style="padding: 8px; background-color: #f5f5f5;">Header 2</th>
            <th style="padding: 8px; background-color: #f5f5f5;">Header 3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px;">Cell 1</td>
            <td style="padding: 8px;">Cell 2</td>
            <td style="padding: 8px;">Cell 3</td>
          </tr>
          <tr>
            <td style="padding: 8px;">Cell 4</td>
            <td style="padding: 8px;">Cell 5</td>
            <td style="padding: 8px;">Cell 6</td>
          </tr>
        </tbody>
      </table>
    `;
    execCommand('insertHTML', html);
  };

  const convertMarkdownToHTML = (markdown) => {
    let html = markdown;

    // Convert headings
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Convert bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Convert ordered lists
    html = html.replace(/^\d+\.\s+(.*)$/gim, '<li>$1</li>');

    // Convert unordered lists
    html = html.replace(/^[-*]\s+(.*)$/gim, '<li>$1</li>');

    // Wrap consecutive <li> tags in appropriate list tags
    html = html.replace(/(<li>.*?<\/li>\n?)+/gs, (match) => {
      // Check if it's an ordered or unordered list based on original markdown
      const lines = match.split('\n').filter(l => l.trim());
      if (lines.length > 0) {
        return '<ul>\n' + match + '</ul>\n';
      }
      return match;
    });

    // Convert line breaks to paragraphs
    html = html.split('\n\n').map(para => {
      para = para.trim();
      if (para && !para.startsWith('<h') && !para.startsWith('<ul') && !para.startsWith('<ol') && !para.startsWith('<li')) {
        return '<p>' + para + '</p>';
      }
      return para;
    }).join('\n');

    return html;
  };

  const pasteMarkdown = () => {
    const markdown = prompt('Paste your Markdown here:');
    if (markdown) {
      const html = convertMarkdownToHTML(markdown);
      editorRef.current.innerHTML = html;
    }
  };

  const pasteHTML = () => {
    const html = prompt('Paste your HTML code here:');
    if (html) {
      editorRef.current.innerHTML = html;
    }
  };

  const isActive = (command) => {
    return document.queryCommandState(command);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">Edit Guide: {itemName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Title Input */}
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder="Guide Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full text-xl font-semibold border-none outline-none"
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-1 p-3 border-b bg-gray-50">
          {/* Text Formatting */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('bold')}
            className={`p-2 rounded ${isActive('bold') ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            <strong>B</strong>
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('italic')}
            className={`p-2 rounded ${isActive('italic') ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            <em>I</em>
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('underline')}
            className={`p-2 rounded ${isActive('underline') ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            <u>U</u>
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Headings */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('formatBlock', '<h1>')}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            H1
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('formatBlock', '<h2>')}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            H2
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('formatBlock', '<h3>')}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            H3
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Lists */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('insertUnorderedList')}
            className={`p-2 rounded ${isActive('insertUnorderedList') ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            UL
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('insertOrderedList')}
            className={`p-2 rounded ${isActive('insertOrderedList') ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            OL
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Alignment */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('justifyLeft')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Left
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('justifyCenter')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Center
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('justifyRight')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Right
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Insert Elements */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertLink}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            Link
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertImage}
            disabled={uploading}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200 disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Image'}
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertTable}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            Table
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={pasteMarkdown}
            className="px-3 py-2 rounded text-sm bg-white hover:bg-gray-200"
          >
            Paste MD
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Quote & Code */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('formatBlock', '<blockquote>')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Quote
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('formatBlock', '<pre>')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Code
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Undo/Redo */}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('undo')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Undo
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execCommand('redo')}
            className="p-2 rounded bg-white hover:bg-gray-200"
          >
            Redo
          </button>
        </div>

        {/* Editor Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div
            ref={editorRef}
            contentEditable
            className="min-h-[300px] max-w-none prose prose-sm sm:prose lg:prose-lg focus:outline-none border rounded p-4"
            style={{
              minHeight: '300px',
              lineHeight: '1.6',
              fontFamily: 'system-ui, sans-serif'
            }}
            data-placeholder="Start writing your guide..."
          />
        </div>

        {/* Hidden file input for image uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Use the toolbar buttons above to format your guide. Click Image to upload photos directly!
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Guide'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleWysiwygEditor;
